import { useState } from 'react'
import { toast } from 'sonner'
import { LoginForm } from '@/features/auth/components/LoginForm'
import { useAuthStore } from '@/stores/auth-store'
import { login as loginRequest, logout as fullLogout } from '@/services/auth/authService'

/**
 * SessionExpiredModal — in-place re-authentication.
 *
 * Shown when the access token expired mid-session AND a silent refresh was not
 * possible (the refresh token is also dead). Because `handleAuthExpiry()` sets
 * `sessionExpired` WITHOUT flipping `isAuthenticated`, the current route/form
 * stays mounted underneath this overlay — so the user's in-progress work is
 * preserved. They re-enter their password (email is locked to their identity)
 * and continue exactly where they left off.
 *
 * Rendered as a self-contained fixed overlay (same pattern as IdleScreensaver)
 * rather than the shared antd Modal — it must be non-dismissable and reliably
 * sized regardless of content height. A hard page refresh (or a sibling tab
 * that silently refreshed) closes it via the store, not via this component.
 */
export function SessionExpiredModal() {
  const sessionExpired = useAuthStore((s) => s.sessionExpired)
  const user = useAuthStore((s) => s.user)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!sessionExpired || !user) return null

  const currentUserId = user.userId
  const currentEmail = user.email

  const handleSubmit = async (_email: string, password: string) => {
    setError(null)
    setIsLoading(true)
    try {
      // Email is locked to the current user, so re-auth as the same identity.
      const result = await loginRequest({ email: currentEmail, password })
      if (result.success && result.user) {
        // Identity guard: never resume on another user's in-memory state.
        if (result.user.userId !== currentUserId) {
          fullLogout()
          return
        }
        // login() already applied the fresh tokens and cleared sessionExpired;
        // set it explicitly too so the overlay closes deterministically.
        useAuthStore.setState({ sessionExpired: false })
        toast.success('Welcome back', {
          description: 'You can continue where you left off.',
        })
      } else {
        setError(result.message || 'Sign in failed. Please try again.')
      }
    } catch {
      setError('Unable to sign in. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div className="w-full max-w-sm rounded-lg border bg-background p-6 shadow-2xl">
        <div className="mb-4 space-y-1">
          <h2 id="session-expired-title" className="text-lg font-semibold text-foreground">
            Session expired
          </h2>
          <p className="text-sm text-muted-foreground">
            Signed in as {currentEmail}. Re-enter your password to continue where you left off — your
            work on this page is preserved.
          </p>
        </div>

        <LoginForm
          onSubmit={handleSubmit}
          isLoading={isLoading}
          error={error}
          lockedEmail={currentEmail}
          submitLabel="Unlock"
        />

        <button
          type="button"
          onClick={() => fullLogout()}
          disabled={isLoading}
          className="mt-4 w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        >
          Log out instead
        </button>
      </div>
    </div>
  )
}
